import { z } from "zod";
import { BranchIdentifierFormValues } from "./branch-identifiers";

const PAYLOAD_TYPE = "branch-masterdata-transfer-v1";

const BranchIdentifierFormDataSchema = z
  .object({
    v1: z.string(),
    formel1: z.string().optional(),
    formel1Ahg: z.string().optional(),
    formel1Ghg: z.string().default(""),
    cross: z.string(),
    fahrschule: z.string().default(""),
    sap: z.string(),
    carano: z.string().default(""),
  })
  .transform(({ formel1, formel1Ahg, formel1Ghg, ...identifiers }) => ({
    ...identifiers,
    formel1Ahg: formel1Ahg ?? formel1 ?? "",
    formel1Ghg,
  }));

const BranchDataSchema = z.object({
  branchIdentifiers: BranchIdentifierFormDataSchema,
  name: z.string(),
  phone: z.string().optional(),
  email: z.string().optional(),
  dunningUser: z.string().optional(),
  dunningRepresentative: z.string().optional(),
  accountingDunningClerkId: z.string().optional(),
  signatureTabletEmail: z.string().optional(),
  companyUuid: z.string(),
  brands: z.array(z.string()).optional(),
  brandRelations: z
    .array(
      z.object({
        brandUuid: z.string(),
        phone: z.string().optional(),
        fax: z.string().optional(),
        email: z.string().optional(),
      }),
    )
    .optional(),
  address: z.object({
    recipientName: z.string().optional(),
    company: z.string().optional(),
    street: z.string(),
    houseNumber: z.string(),
    houseExtension: z.string().optional(),
    postalCode: z.string(),
    city: z.string(),
    country_code: z.string(),
    latitude: z.string(),
    longitude: z.string(),
  }),
});

const PayloadSchema = z.object({
  type: z.literal(PAYLOAD_TYPE),
  data: BranchDataSchema,
});

export type BranchClipboardData = z.infer<typeof BranchDataSchema>;

export function serializeBranchClipboardData(data: BranchClipboardData): string {
  return JSON.stringify({ type: PAYLOAD_TYPE, data });
}

export function parseBranchClipboardData(rawValue: string): BranchClipboardData | null {
  try {
    const parsedValue: unknown = JSON.parse(rawValue);
    const result = PayloadSchema.safeParse(parsedValue);
    return result.success ? result.data.data : null;
  } catch {
    return null;
  }
}

export function toBranchClipboardData(formValues: {
  branchIdentifiers: BranchIdentifierFormValues;
  name: string;
  phone?: string;
  email?: string;
  dunningUser?: string;
  dunningRepresentative?: string;
  accountingDunningClerkId?: string;
  signatureTabletEmail?: string;
  companyUuid: string;
  brands?: string[];
  brandRelations?: { brandUuid: string; phone?: string; fax?: string; email?: string }[];
  address: {
    recipientName?: string | null;
    company?: string | null;
    street: string;
    houseNumber: string;
    houseExtension?: string | null;
    postalCode: string;
    city: string;
    country_code: string;
    latitude?: string | null;
    longitude?: string | null;
  };
}): BranchClipboardData {
  return {
    branchIdentifiers: formValues.branchIdentifiers,
    name: formValues.name,
    phone: formValues.phone ?? undefined,
    email: formValues.email ?? undefined,
    dunningUser: formValues.dunningUser ?? undefined,
    dunningRepresentative: formValues.dunningRepresentative ?? undefined,
    accountingDunningClerkId: formValues.accountingDunningClerkId ?? undefined,
    signatureTabletEmail: formValues.signatureTabletEmail ?? undefined,
    companyUuid: formValues.companyUuid,
    brands: formValues.brands ?? undefined,
    brandRelations: formValues.brandRelations ?? undefined,
    address: {
      recipientName: formValues.address.recipientName ?? undefined,
      company: formValues.address.company ?? undefined,
      street: formValues.address.street,
      houseNumber: formValues.address.houseNumber,
      houseExtension: formValues.address.houseExtension ?? undefined,
      postalCode: formValues.address.postalCode,
      city: formValues.address.city,
      country_code: formValues.address.country_code,
      latitude: formValues.address.latitude ?? "",
      longitude: formValues.address.longitude ?? "",
    },
  };
}

export function toBranchFormValues(clipboardData: BranchClipboardData): {
  branchIdentifiers: BranchIdentifierFormValues;
  name: string;
  phone: string;
  email: string;
  dunningUser: string;
  dunningRepresentative: string;
  accountingDunningClerkId: string;
  signatureTabletEmail: string;
  companyUuid: string;
  brands: string[];
  brandRelations: { brandUuid: string; phone?: string; fax?: string; email?: string }[];
  address: {
    recipientName: string;
    company: string;
    street: string;
    houseNumber: string;
    houseExtension: string;
    postalCode: string;
    city: string;
    country_code: string;
    latitude: string;
    longitude: string;
  };
} {
  return {
    branchIdentifiers: clipboardData.branchIdentifiers,
    name: clipboardData.name,
    phone: clipboardData.phone ?? "",
    email: clipboardData.email ?? "",
    dunningUser: clipboardData.dunningUser ?? "",
    dunningRepresentative: clipboardData.dunningRepresentative ?? "",
    accountingDunningClerkId: clipboardData.accountingDunningClerkId ?? "",
    signatureTabletEmail: clipboardData.signatureTabletEmail ?? "",
    companyUuid: clipboardData.companyUuid,
    brands: clipboardData.brands ?? [],
    brandRelations: clipboardData.brandRelations ?? [],
    address: {
      recipientName: clipboardData.address.recipientName ?? "",
      company: clipboardData.address.company ?? "",
      street: clipboardData.address.street,
      houseNumber: clipboardData.address.houseNumber,
      houseExtension: clipboardData.address.houseExtension ?? "",
      postalCode: clipboardData.address.postalCode,
      city: clipboardData.address.city,
      country_code: clipboardData.address.country_code,
      latitude: clipboardData.address.latitude,
      longitude: clipboardData.address.longitude,
    },
  };
}
